import React, { Component } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';

const ONBOARDING_STORAGE_KEY = 'GoRepaOnboardingCompleted_v1';

const tourSteps = [
    {
        target: '.demo-label',
        title: 'Welcome to GoRepa!',
        content: (
            <div>
                <p style={{ marginBottom: '8px' }}>
                    The properties you see here are <b>demo properties</b> that have already been
                    discovered by the platform.
                </p>
                <p style={{ marginBottom: '0' }}>
                    They're pre-loaded so you can explore how the analytics and indicators work
                    before adding your own. <b>Click any green or red title to see indicator details.</b>
                </p>
            </div>
        ),
        disableBeacon: true,
        placement: 'bottom',
    },
    {
        target: '.add-btn',
        title: 'Adding a Property',
        content: (
            <div>
                <p style={{ marginBottom: '8px' }}>
                    Click <b>ADD</b> to add a property to your portfolio. You have two options:
                </p>
                <ul style={{ paddingLeft: '18px', marginBottom: '0' }}>
                    <li style={{ marginBottom: '4px' }}>
                        <b>Enter the details manually</b>
                    </li>
                    <li>
                        Use the <b>LOOK UP</b> feature to have the platform find the property and run the full financial analysis for you.
                    </li>
                </ul>
            </div>
        ),
        placement: 'bottom',
        spotlightPadding: 6,
    },
    {
        target: '#alerts',
        title: 'Advanced Search',
        content: (
            <div>
                <p style={{ marginBottom: '0' }}>
                    Create an <b>Advanced Search</b> to have the platform automatically find and analyze properties that match your criteria (location, price, bedrooms, and more) — and notify you when new ones show up.
                </p>
            </div>
        ),
        placement: 'right',
        spotlightPadding: 6,
    },
    {
        target: 'body',
        title: "You're all set!",
        content: (
            <div>
                <p style={{ marginBottom: '8px' }}>
                    Go ahead—<b>look up a property</b> or set up an <b>Advanced Search</b> to have the platform find properties for you daily.
                </p>
                <p style={{ marginBottom: '0' }}>
                    That's when GoRepa's analytics really shine — you'll instantly see profitability
                    indicators, risk scores, and market metrics for properties you actually care
                    about.
                </p>
            </div>
        ),
        placement: 'center',
        disableBeacon: true,
    },
];

const joyrideStyles = {
    options: {
        primaryColor: '#f4b619',
        textColor: '#333',
        backgroundColor: '#ffffff',
        arrowColor: '#ffffff',
        overlayColor: 'rgba(0, 0, 0, 0.65)',
        zIndex: 10000,
        width: 380,
    },
    tooltipContainer: {
        textAlign: 'left',
    },
    tooltipTitle: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#0032b6',
        marginBottom: '8px',
    },
    tooltipContent: {
        fontSize: '14px',
        padding: '6px 0',
    },
    buttonNext: {
        backgroundColor: '#f4b619',
        color: '#000',
        fontWeight: 'bold',
        borderRadius: '4px',
        padding: '8px 16px',
    },
    buttonBack: {
        color: '#0032b6',
        marginRight: '8px',
    },
    buttonSkip: {
        color: '#888',
    },
    buttonClose: {
        display: 'none',
    },
};

class OnboardingTour extends Component {
    state = {
        run: false,
        stepIndex: 0,
    };

    componentDidMount() {
        this.maybeStartTour();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.profile !== this.props.profile || prevProps.user !== this.props.user) {
            this.maybeStartTour();
        }
    }

    maybeStartTour = () => {
        if (this.state.run || this.pollTimer) {
            return;
        }

        if (this.hasSeenTour()) {
            return;
        }

        if (!this.shouldShowForUser()) {
            return;
        }

        // Poll until the first tour target actually exists in the DOM.
        // Dashboard/PropertyView mount asynchronously after profile loads.
        let attempts = 0;
        const maxAttempts = 30; // ~9 seconds
        this.pollTimer = setInterval(() => {
            attempts += 1;
            const firstTargetReady =
                document.querySelector('.demo-label') && document.querySelector('.add-btn');
            if (firstTargetReady) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
                this.setState({ run: true, stepIndex: 0 });
            } else if (attempts >= maxAttempts) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        }, 300);
    };

    componentWillUnmount() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    hasSeenTour = () => {
        try {
            return localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'Y';
        } catch (e) {
            return false;
        }
    };

    markTourSeen = () => {
        try {
            localStorage.setItem(ONBOARDING_STORAGE_KEY, 'Y');
        } catch (e) {
            // ignore
        }
    };

    shouldShowForUser = () => {
        const { profile, user } = this.props;
        // Only auto-start when the user is looking at the demo profile
        // (that's the state new trial users land in after signup).
        if (!profile || !user) {
            return false;
        }
        return profile.type === 'demo';
    };

    handleJoyrideCallback = (data) => {
        const { status, action, index, type } = data;

        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status) || action === ACTIONS.CLOSE) {
            this.markTourSeen();
            this.setState({ run: false, stepIndex: 0 });
            return;
        }

        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
            const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
            this.setState({ stepIndex: nextIndex });
        }
    };

    render() {
        const { run, stepIndex } = this.state;

        return (
            <Joyride
                steps={tourSteps}
                run={run}
                stepIndex={stepIndex}
                continuous={true}
                showProgress={true}
                showSkipButton={true}
                scrollToFirstStep={true}
                disableOverlayClose={true}
                spotlightClicks={false}
                styles={joyrideStyles}
                locale={{
                    back: 'Back',
                    close: 'Close',
                    last: 'Got it!',
                    next: 'Next',
                    skip: 'Skip tour',
                }}
                callback={this.handleJoyrideCallback}
            />
        );
    }
}

export default OnboardingTour;
